// src/features/auth/auth.store.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthUser } from '../../api/types';
import { clearStoredToken, getStoredToken, storeToken } from '../../api/client';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (user: AuthUser) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,

      login: (user: AuthUser) => {
        storeToken(user.token);
        set({ user, isAuthenticated: true });
      },

      logout: () => {
        clearStoredToken();
        set({ user: null, isAuthenticated: false });
      },
    }),
    {
      name: 'hams-auth',
      // Persist user metadata (token stored separately in session/localStorage)
      partialize: (state) => ({
        user: state.user
          ? { ...state.user, token: '' } // don't persist raw token in Zustand storage
          : null,
        isAuthenticated: state.isAuthenticated,
      }),
      // On rehydration: restore token from session/localStorage into the user object
      onRehydrateStorage: () => (state) => {
        if (state?.user && state.isAuthenticated) {
          const token = getStoredToken();
          if (token) {
            state.user = { ...state.user, token };
          } else {
            // Token expired/cleared — force logout
            state.user = null;
            state.isAuthenticated = false;
          }
        }
      },
    }
  )
);
